import { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { adminClient } from "@/lib/api";

export const InstagramJobGenerator = ({ draft, description, onDescription, fields, onGenerated, busy, generating, onBusy }) => {
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const request = useRef(null);
    useEffect(() => () => request.current?.abort(), []);
    const generate = async () => {
        if (busy || request.current) return;
        const controller = new AbortController();
        request.current = controller;
        setMessage(""); setError(""); onBusy(true);
        try {
            const { data } = await adminClient().post(`/admin/instagram/drafts/${draft.id}/generate-details`,
                { job_description: description }, { signal: controller.signal, timeout: 90000 });
            if (controller.signal.aborted) return;
            const additions = Object.fromEntries(Object.entries(data.fields).filter(([key, value]) => !fields[key]?.trim() && value));
            onGenerated(additions);
            const count = Object.keys(additions).length;
            setMessage(`${count ? `${count} empty ${count === 1 ? "field filled" : "fields filled"}. Not saved yet.` : "No additional confirmed details found."} Existing details kept. ${data.warnings.join(" ")}`);
        } catch (e) {
            if (!controller.signal.aborted) setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't generate job details. Your edits are unchanged. Please try again.");
        } finally {
            if (!controller.signal.aborted) onBusy(false);
            request.current = null;
        }
    };
    return <div className="min-w-0 space-y-3 border-y border-white/10 py-5" data-testid="instagram-job-generator">
        <div>
            <Label htmlFor="instagram-job-description" className="text-xs text-zinc-300">Your job description <span className="text-zinc-500">(optional)</span></Label>
            <Textarea id="instagram-job-description" data-testid="instagram-job-description" value={description} disabled={busy}
                onChange={event => onDescription(event.target.value)} maxLength={5000} rows={3}
                placeholder="Vehicle, work completed, symptoms, confirmed diagnosis, time on site…"
                className="mt-1 min-h-20 border-zinc-700 bg-zinc-950 text-white" />
        </div>
        <button type="button" onClick={generate} disabled={busy || !(description.trim() || draft.instagram_caption.trim())}
            data-testid="instagram-generate-details" className="inline-flex max-w-full items-center gap-2 rounded-sm bg-red-600 px-3 py-2 text-left text-sm font-semibold text-white transition-colors hover:bg-red-500 disabled:opacity-40">
            {generating ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Sparkles className="h-4 w-4 shrink-0" />}
            {generating ? "Generating job details…" : "Generate Job Details with AI"}
        </button>
        <p data-testid="instagram-ai-policy" className="text-xs text-zinc-500">Caption + your description only. Unconfirmed details stay blank.</p>
        {message && <p role="status" data-testid="instagram-generation-result" className="break-words text-xs text-emerald-300">{message}</p>}
        {error && <p role="alert" data-testid="instagram-generation-error" className="break-words text-xs text-red-300">{error}</p>}
    </div>;
};